import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { sessionStorage } from "aws-amplify/utils";
import "aws-amplify/auth/enable-oauth-listener";
import "quill/dist/quill.snow.css";
import "./index.css";
import App from "./App.tsx";
import { AuthProvider } from "./context/AuthContext";
import { EntitlementsProvider } from "./context/EntitlementsContext";
import { PaywallProvider } from "./context/PaywallContext";

// Configure Amplify with Cognito User Pool + OAuth (Google)
const isLocalHostname = (hostname: string): boolean =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname === "0.0.0.0";

const isBackendStyleRedirect = (url: URL): boolean => {
  const hostnameLooksApi = /^api\./i.test(url.hostname);
  const normalizedPath = url.pathname.toLowerCase();
  return (
    hostnameLooksApi ||
    normalizedPath.includes("/auth/google/callback") ||
    normalizedPath.startsWith("/api/")
  );
};

const resolveAppUrl = (): string => {
  const configuredUrl =
    (import.meta.env.VITE_APP_URL as string | undefined)?.trim() ?? "";

  if (typeof window === "undefined") {
    return configuredUrl;
  }

  const origin = window.location.origin;
  const currentHost = window.location.hostname;
  const isCurrentLocal = isLocalHostname(currentHost);

  if (!configuredUrl) {
    return origin;
  }

  try {
    const configured = new URL(configuredUrl);
    const configuredIsLocal = isLocalHostname(configured.hostname);

    // Safety guard: never use localhost callback URLs on non-local hosts.
    if (!isCurrentLocal && configuredIsLocal) {
      console.warn(
        "VITE_APP_URL points to localhost on a non-local host. Falling back to current origin.",
      );
      return origin;
    }

    // Safety guard: reject backend callback URLs for Cognito Hosted UI return.
    if (!isCurrentLocal && isBackendStyleRedirect(configured)) {
      console.warn(
        "VITE_APP_URL points to a backend/API callback URL. Falling back to current frontend origin.",
      );
      return origin;
    }
  } catch {
    console.warn("VITE_APP_URL is invalid. Falling back to current origin.");
    return origin;
  }

//Hllo    


  return configuredUrl;
};

const appUrl = resolveAppUrl();

// Get the current environment's origin dynamically
const currentOrigin = typeof window !== "undefined" ? window.location.origin : appUrl;

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
      loginWith: {
        oauth: {
          domain: import.meta.env.VITE_COGNITO_DOMAIN as string,
          scopes: [
            "openid",
            "email",
            "profile",
            "aws.cognito.signin.user.admin",
          ],
          
          // Dynamically picks the correct URL for Prod or Local
          redirectSignIn: [
            currentOrigin, 
            appUrl
          ],
          
          // Drops the user exactly on the login page after AWS session wipe
          redirectSignOut: [
            `${currentOrigin}/login`,
            `${appUrl}/login`
          ],
          responseType: "code",
        },
      },
    },
  },
});

if (
  typeof window !== "undefined" &&
  window.localStorage.getItem("rememberMePreference") === "false"
) {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
}

// ---------------------------------------------------------------------------
// No Hub.listen retry logic needed. The backend Pre Sign-up Lambda handles
// Automatic Account Linking (Magic UX) — it links the Google identity to
// any existing native Cognito user and returns the event successfully.
// The user is signed in seamlessly on the first attempt.
// ---------------------------------------------------------------------------

createRoot(document.getElementById("root")!).render(
  <BrowserRouter
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    }}
  >
    <AuthProvider>
      {/* Entitlements sit inside Auth (they depend on the session) and outside
          Paywall (the modal reads the current plan to mark owned tiers). */}
      <EntitlementsProvider>
        <PaywallProvider>
          <App />
        </PaywallProvider>
      </EntitlementsProvider>
    </AuthProvider>
  </BrowserRouter>,
);